import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import Input from 'components/Input'
import TempValueWrapper from 'components/TempValueWrapper'
import { findOneSubject, findOneSubjectVariables } from '__generatedTypes__/findOneSubject'
import { updateSubject, updateSubjectVariables } from '__generatedTypes__/updateSubject'
import { FIND_ONE_SUBJECT, UPDATE_SUBJECT } from 'queries/subject'
import { toast } from 'utils/toast'

function arrayToObject(arr: string[]): Record<number, string> {
  return arr.reduce((acc, val, index) => {
    if (val !== '' || index in arr) {
      acc[index] = val
    }
    return acc
  }, {} as Record<number, string>)
}

function objectToArray(obj: Record<number, string>): string[] {
  const keys = Object.keys(obj).map(Number)
  const lastNonEmptyIndex = Math.max(...keys.filter((key) => obj[key]))

  return Array.from({ length: lastNonEmptyIndex + 1 }, (_, i) => obj[i] ?? '')
}

export type DescriptionFormProps = {
  subjectId: string
  tabIndex?: number
}

export const DescriptionForm = ({ subjectId, tabIndex = 1 }: DescriptionFormProps) => {
  const descriptionIndex = Math.max(tabIndex - 1, 0)

  const apolloClient = useApolloClient()

  const { data, loading } = useQuery<findOneSubject, findOneSubjectVariables>(FIND_ONE_SUBJECT, {
    variables: {
      id: subjectId,
    },
    fetchPolicy: 'network-only',
  })
  const questionBankDescriptions = arrayToObject(data?.findOneSubject?.questionBankDescription ?? [])
  const description = questionBankDescriptions[descriptionIndex] ?? ''
  const [updateSubject, { loading: submitting }] = useMutation<updateSubject, updateSubjectVariables>(UPDATE_SUBJECT)

  const handleChange = async (value: string) => {
    try {
      const questionBankDescriptionsUpdated = objectToArray({
        ...questionBankDescriptions,
        [descriptionIndex]: value.trim(),
      })

      await updateSubject({
        variables: {
          params: {
            id: subjectId,
            questionBankDescription: questionBankDescriptionsUpdated,
          },
        },
      })

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'SubjectType',
          id: subjectId,
        }),
        fields: {
          questionBankDescription: () => questionBankDescriptionsUpdated,
        },
      })

      toast.success('Description updated successfully')
    } catch (error) {
      toast.error('Failed to update description')
    }
  }

  return (
    <TempValueWrapper value={description} onChange={handleChange} disabled={loading || submitting}>
      {({ value, onChange, editable }) => (
        <Input
          label="Description"
          placeholder="Enter Description"
          value={value}
          onChange={onChange}
          disabled={!editable || loading || submitting}
          multiline
          fullWidth
        />
      )}
    </TempValueWrapper>
  )
}
